"use client";

import { useEffect, useMemo, useRef } from "react";
import { useTokenStore, useWalletStore } from "@/lib/zustand";
import { useSwitchChain, useChainId } from "wagmi";
import { useParaAccount } from "@/hooks/use-appkit-account";
import { useWallet, useClient, useWalletState } from "@getpara/react-sdk";
import { ChainId, ChainType } from "@lifi/sdk";

// Retry helper for EVM wallet lookup - handles case where para.init() hasn't hydrated yet
const getEvmWalletsWithRetry = async (
  para: ReturnType<typeof useClient>,
  maxRetries = 3,
  delayMs = 500
): Promise<any[]> => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const wallets = para?.getWalletsByType?.("EVM") || [];
    if (wallets.length > 0) return wallets;
    if (attempt < maxRetries - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return [];
};

export const useAutoSwitchChain = () => {
  const justMounted = useRef(true);
  const isSwitchingRef = useRef(false);
  // Cache wallet IDs - Para SDK can lose them after switching
  const cachedSolanaWalletIdRef = useRef<string | null>(null);
  const cachedEvmWalletIdRef = useRef<string | null>(null);
  const { fromToken, fromChain } = useTokenStore();
  const { isEmailOrSocialWallet, setIsEmailOrSocialWallet } = useWalletStore();
  const { switchChain } = useSwitchChain();
  const chainId = useChainId();
  const { isConnected, walletType: paraWalletType, isExternalEvm } = useParaAccount();
  const { data: wallet } = useWallet();
  const para = useClient();
  const { setSelectedWallet } = useWalletState();

  // Stabilize para client reference
  const paraRef = useRef(para);
  useEffect(() => { paraRef.current = para; }, [para]);

  // Stabilize setSelectedWallet reference
  const setSelectedWalletRef = useRef(setSelectedWallet);
  useEffect(() => { setSelectedWalletRef.current = setSelectedWallet; }, [setSelectedWallet]);

  // Extract wallet type for stable dependency
  const walletType = (wallet as any)?.type;

  // Detect email/social wallet and update shared store (only goes true, never false)
  useEffect(() => {
    const detected = !!(
      (wallet as any)?.email ||
      (wallet as any)?.userId ||
      (wallet as any)?.externalId
    );
    if (detected) {
      setIsEmailOrSocialWallet(true);
    }
  }, [wallet, setIsEmailOrSocialWallet]);

  const desired = useMemo(() => {
    // Prefer chain from selected input token, fallback to store's fromChain
    const desiredChainId = fromToken?.chainId ?? fromChain?.id;
    let desiredChainType: "EVM" | "SVM" | undefined = fromChain?.chainType as any;
    if (!desiredChainType && desiredChainId !== undefined) {
      desiredChainType = desiredChainId === ChainId.SOL ? "SVM" : "EVM";
    }
    // Para wallet awareness: if active wallet is SOLANA and token chain is not set, prefer SVM
    if (!fromToken?.chainId && walletType === "SOLANA") {
      return { desiredChainId: ChainId.SOL, desiredChainType: "SVM" as const };
    }
    return { desiredChainId, desiredChainType } as {
      desiredChainId?: number;
      desiredChainType?: "EVM" | "SVM";
    };
  }, [fromToken?.chainId, fromChain?.id, fromChain?.chainType, walletType]);

  useEffect(() => {
    const isFirstMount = justMounted.current;
    if (isFirstMount) {
      justMounted.current = false;
    }

    // For email/social wallets, try switching wallet type using setCurrentWalletIds
    // Use the shared store value to prevent logout during wallet state transitions
    if (isEmailOrSocialWallet) {
      // If already switching, return early to prevent repeated attempts
      if (isSwitchingRef.current) return;

      try {
        if (desired.desiredChainType === "SVM" && (wallet as any)?.type !== "SOLANA") {
          // Skip Solana switch on first mount to prevent post-login logout issues
          if (isFirstMount) return;

          const solWallets = paraRef.current?.getWalletsByType?.("SOLANA") || [];
          // Cache wallet ID when found
          if (solWallets.length > 0 && solWallets[0]?.id) {
            cachedSolanaWalletIdRef.current = solWallets[0].id;
          }
          // Use fresh wallet ID or fall back to cached
          const solWalletId = solWallets[0]?.id || cachedSolanaWalletIdRef.current;
          if (solWalletId) {
            isSwitchingRef.current = true;
            // Preserve existing wallet types when switching — don't wipe EVM when switching to Solana
            const existing = paraRef.current?.currentWalletIds ?? {};
            paraRef.current?.setCurrentWalletIds?.({ ...existing, SOLANA: [solWalletId] });
            setSelectedWalletRef.current?.({ id: solWalletId, type: 'SOLANA' });
            setTimeout(() => { isSwitchingRef.current = false; }, 5000);
          }
        } else if (desired.desiredChainType === "EVM" && (wallet as any)?.type !== "EVM") {
          // Allow EVM switch on first mount - this is needed for EVM swaps to work on page load
          // Use retry to handle case where para.init() hasn't hydrated wallet registry yet
          (async () => {
            const evmWallets = await getEvmWalletsWithRetry(paraRef.current);
            // Cache wallet ID when found
            if (evmWallets.length > 0 && evmWallets[0]?.id) {
              cachedEvmWalletIdRef.current = evmWallets[0].id;
            }
            // Use fresh wallet ID or fall back to cached
            const evmWalletId = evmWallets[0]?.id || cachedEvmWalletIdRef.current;
            if (evmWalletId) {
              isSwitchingRef.current = true;
              // Preserve existing wallet types when switching — don't wipe Solana when switching to EVM
              const existing = paraRef.current?.currentWalletIds ?? {};
              paraRef.current?.setCurrentWalletIds?.({ ...existing, EVM: [evmWalletId] });
              setSelectedWalletRef.current?.({ id: evmWalletId, type: 'EVM' });
              setTimeout(() => { isSwitchingRef.current = false; }, 5000);
            }
          })();
        }
      } catch (e) {
        // Para SDK error during wallet switch - no action needed
      }
      return;
    }

    if (!isConnected) return;
    if (!desired.desiredChainId || !desired.desiredChainType) return;

    if (desired.desiredChainType === "EVM") {
      const activeWalletType = (wallet as any)?.type;
      if (activeWalletType !== "EVM") {
        // Skip external wallet EVM switch on first mount to prevent Phantom popup
        if (isFirstMount) return;

        // Use retry to handle case where para.init() hasn't hydrated wallet registry yet
        (async () => {
          try {
            const evmWallets = await getEvmWalletsWithRetry(paraRef.current);
            // Cache wallet ID when found
            if (evmWallets.length > 0 && evmWallets[0]?.id) {
              cachedEvmWalletIdRef.current = evmWallets[0].id;
            }
            const evmWalletId = evmWallets[0]?.id || cachedEvmWalletIdRef.current;
            if (evmWalletId && !isSwitchingRef.current) {
              isSwitchingRef.current = true;
              setSelectedWalletRef.current?.({ id: evmWalletId, type: 'EVM' });
              setTimeout(() => { isSwitchingRef.current = false; }, 5000);
            }
          } catch {
            // External wallet error - no action needed
          }
        })();
        return;
      }
      // Skip switchChain for Para internal EVM wallets - Para's executeRoute handles
      // chain switching internally via para.init(). Only call switchChain for external wallets.
      if (paraWalletType === 'EVM' && !isExternalEvm) {
        return;
      }
      if (chainId !== desired.desiredChainId && desired.desiredChainId !== undefined) {
        try {
          switchChain({ chainId: desired.desiredChainId });
        } catch {
          // Chain switch error - no action needed
        }
      }
      return;
    }

    if (desired.desiredChainType === "SVM") {
      // Skip Solana switch on first mount to prevent post-login logout issues
      if (isFirstMount) return;

      const isSolanaActive = (wallet as any)?.type === "SOLANA";
      if (!isSolanaActive) {
        try {
          const solWallets = paraRef.current?.getWalletsByType?.("SOLANA") || [];
          // Cache wallet ID when found
          if (solWallets.length > 0 && solWallets[0]?.id) {
            cachedSolanaWalletIdRef.current = solWallets[0].id;
          }
          const solWalletId = solWallets[0]?.id || cachedSolanaWalletIdRef.current;
          if (solWalletId && !isSwitchingRef.current) {
            isSwitchingRef.current = true;
            setSelectedWalletRef.current?.({ id: solWalletId, type: 'SOLANA' });
            setTimeout(() => { isSwitchingRef.current = false; }, 5000);
          }
        } catch {
          // External wallet error - no action needed
        }
      }
    }
  }, [desired.desiredChainId, desired.desiredChainType, chainId, isConnected, switchChain, walletType, isEmailOrSocialWallet]);
};
